import Disclaimer from '../components/Disclaimer'
import { Link } from 'react-router-dom'
import Navigation from '../components/Navigation'
import { ROUTES } from '../Routes'
import { usePageTitle } from '../hooks/usePageTitle'

const CCPAPrivacyNoticePage = () => {
    usePageTitle('CCPA Privacy Notice');
    return (
        <div id="body" className="page-template-default page page-id-255 wp-embed-responsive ccpa-privacy-notice geo-az bid-60">
            <Disclaimer />
            <Navigation />
            <main>
                <section id="intro"
                    className="w-full relative flex items-center min-h-[134px] sm:min-h-[294px] md:min-h-[266px] lg:min-h-[230px]  bg-hero ">
                    <div className="hero my-auto flex flex-row items-center">
                        <div className="z-[1] flex-1 container">
                            <div className="py-[24px] sm:py-[32px] lg:py-[48px]">
                                <h1 className="leading-[40px] text-[34px] sm:leading-[40px] font-semibold text-[#323738] m-0">
                                    CCPA Privacy Notice
                                </h1>
                                <div
                                    className="text-[#323738] mt-[8px] text-[14px] leading-[20px] sm:text-[16px] md:leading-[20px] lg:leading-[24px] ">
                                    <p>
                                        [Last Modified: February 11, 2025]
                                    </p>

                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <div className="container !mt-[20px]">

                    <div className="flex justify-between flex-col lg:flex-row-reverse gap-[24px]">
                        <div id="content-area" className="legal-page">
                            <p>
                                <span className="font-bold underline">APPLICABILITY:</span> The California Consumer Privacy Act of
                                2018 (“<strong>CCPA</strong>”), the California Privacy Rights Act of 2020 effective January 1,
                                2023 (“<strong>CPRA</strong>”), any other California privacy laws, and this CCPA Notice apply to
                                visitors, users, and independent contractors, and others who are California residents
                                (“<strong>consumers</strong>” or “<strong>you</strong>”). Any terms defined in the CCPA and CPRA
                                have the same meaning when used in this CCPA Notice. This CCPA Notice applies to California
                                residents’ Personal Information, which we collect directly or indirectly while you are using our
                                service or in order to provide our services, or employee and business-to-business Personal
                                Information.
                            </p>
                            <p>
                                This CCPA Notice is an integral part of our Privacy Policy, and thus, definitions used herein
                                shall have the same meaning as defined in the <Link to={ROUTES.PRIVACY_POLICY} target="_blank" rel="noreferrer">Privacy
                                    Policy</Link>.
                            </p>
                            <br />

                            <h3 className="!font-bold underline" id="part1">
                                PART I: A COMPREHENSIVE DESCRIPTION OF THE INFORMATION PRACTICES:
                            </h3>
                            <p className="!font-bold">
                                (A) <span className="underline">CATEGORIES OF PERSONAL INFORMATION WE COLLECT</span>
                            </p>
                            <p>We collect Personal Information which is defined under the CCPA as any information that
                                identifies, relates to, describes, references, is capable of being associated with, or could
                                reasonably be linked, directly or indirectly, with a particular consumer, household or device,
                                all as detailed in the table below.</p>
                            <p>Personal Information further includes Sensitive Personal Information (“<strong>SPI</strong>”) as
                                detailed in the table below.</p>
                            <p>Personal Information does not include: Publicly available information that is lawfully made
                                available from government records, that a consumer has otherwise made available to the public;
                                de-identified or aggregated consumer information; information excluded from the CCPA’s or CPRA’s
                                scope, such as: Health or medical information covered by the Health Insurance Portability and
                                Accountability Act of 1996 (HIPPA) and the California Confidentiality of Medical Information Act
                                (CMIA) or clinical trial data; Personal information covered by certain sector-specific privacy
                                laws, including the Fair Credit Reporting Act (FRCA), the Gramm-Leach-Bliley Act (GLBA) or
                                California Financial Information Privacy Act (FIPA) and the Driver’s Privacy Protection Act of
                                1994.</p>
                            <p className="!font-bold italic">
                                Ryze Beyond Ltd. have collected the following categories of personal information within the last
                                twelve (12) months:
                            </p>
                            <table>
                                <tbody>
                                    <tr>
                                        <th className="text-center">Category</th>
                                        <th className="text-center">Example</th>
                                        <th className="text-center">Collected</th>
                                    </tr>

                                    <tr className="text-justify">
                                        <td>
                                            <p>A. Identifiers.</p>
                                        </td>
                                        <td>
                                            <p>A real name, alias, postal address, unique personal identifier, online identifier,
                                                Internet Protocol address, email address, account name, Social Security number,
                                                driver's license number, passport number, or other similar identifiers.</p>
                                        </td>
                                        <td>
                                            <p><span className="!font-bold">Yes:</span> online identifiers, Internet protocol address,
                                                unique identifiers, real name, email address.</p>
                                        </td>
                                    </tr>
                                    <tr className="text-justify">
                                        <td>
                                            <p>B. Personal information categories listed in the California Customer Records statute
                                                (Cal. Civ. Code § 1798.80(e)).</p>
                                        </td>
                                        <td>
                                            <p>A name, signature, Social Security number, physical characteristics or description,
                                                address, telephone number, passport number, driver's license or state identification
                                                card number, insurance policy number, education, employment, employment history,
                                                bank account number, credit card number, debit card number, or any other financial
                                                information, medical information, or health insurance information.</p>
                                            <p>Some personal information included in this category may overlap with other
                                                categories.</p>
                                        </td>
                                        <td>
                                            <p><span className="!font-bold">Yes:</span> name, email address.</p>
                                        </td>
                                    </tr>
                                    <tr className="text-justify">
                                        <td>
                                            <p>C. Protected classification characteristics under California or federal law.</p>
                                        </td>
                                        <td>
                                            <p>Age (40 years or older), race, color, ancestry, national origin, citizenship,
                                                religion or creed, marital status, medical condition, physical or mental disability,
                                                sex (including gender, gender identity, gender expression, pregnancy or childbirth
                                                and related medical conditions), sexual orientation, veteran or military status,
                                                genetic information (including familial genetic information).</p>
                                        </td>
                                        <td>
                                            <p><strong>No.</strong></p>
                                        </td>
                                    </tr>
                                    <tr className="text-justify">
                                        <td>
                                            <p>D. Commercial information.</p>
                                        </td>
                                        <td>
                                            <p>Records of personal property, products or services purchased, obtained, or
                                                considered, or other purchasing or consuming histories or tendencies.</p>
                                        </td>
                                        <td>
                                            <p><span className="!font-bold">Yes:</span> transactions conducted to audit conversion and
                                                manage business transactions with our Partners.</p>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <p>E. Biometric information.</p>
                                        </td>
                                        <td>
                                            <p>Genetic, physiological, behavioral, and biological characteristics, or activity
                                                patterns used to extract a template or other identifier or identifying information,
                                                such as, fingerprints, faceprints, and voiceprints, iris or retina scans, keystroke,
                                                gait, or other physical patterns, and sleep, health, or exercise data.</p>
                                        </td>
                                        <td>
                                            <p className="!font-bold">No.</p>
                                        </td>
                                    </tr>
                                    <tr className="text-justify">
                                        <td>
                                            <p>F. Internet or other similar network activity.</p>
                                        </td>
                                        <td>
                                            <p>Browsing history, search history, information on a consumer's interaction with a
                                                website, application, or advertisement.</p>
                                        </td>
                                        <td>
                                            <p><span className="!font-bold">Yes:</span> the user's interaction with our website and
                                                advertisements.</p>
                                        </td>
                                    </tr>
                                    <tr className="text-justify">
                                        <td>
                                            <p>G. Geolocation data.</p>
                                        </td>
                                        <td>
                                            <p>Physical location, approximate location derived from IP address or movements.</p>
                                        </td>
                                        <td>
                                            <p><span className="!font-bold">Yes:</span> approximate location derived from IP address or
                                                provided by the user.</p>
                                        </td>
                                    </tr>
                                    <tr className="text-justify">
                                        <td>
                                            <p>H. Sensory data.</p>
                                        </td>
                                        <td>
                                            <p>Audio, electronic, visual, thermal, olfactory, or similar information.</p>
                                        </td>
                                        <td>
                                            <p className="!font-bold">No.</p>
                                        </td>
                                    </tr>
                                    <tr className="text-justify">
                                        <td>
                                            <p>I. Professional or employment-related information.</p>
                                        </td>
                                        <td>
                                            <p>Current or past job history or performance evaluations.</p>
                                        </td>
                                        <td>
                                            <p className="!font-bold">No.</p>
                                        </td>
                                    </tr>
                                    <tr className="text-justify">
                                        <td>
                                            <p>J. Non-public education information (per the Family Educational Rights and Privacy
                                                Act (20 U.S.C. Section 1232g, 34 C.F.R. Part 99)).</p>
                                        </td>
                                        <td>
                                            <p>Education records directly related to a student maintained by an educational
                                                institution or party acting on its behalf, such as grades, transcripts, class lists,
                                                student schedules, student identification codes, student financial information, or
                                                student disciplinary records.</p>
                                        </td>
                                        <td>
                                            <p className="!font-bold">No.</p>
                                        </td>
                                    </tr>
                                    <tr className="text-justify">
                                        <td>
                                            <p>K. Inferences drawn from other personal information.</p>
                                        </td>
                                        <td>
                                            <p>Profile reflecting a person's preferences, characteristics, psychological trends,
                                                predispositions, behavior, attitudes, intelligence, abilities, and aptitudes.</p>
                                        </td>
                                        <td>
                                            <p className="!font-bold">No.</p>
                                        </td>
                                    </tr>
                                    <tr className="text-justify">
                                        <td>
                                            <p>L. Sensitive personal information.</p>
                                        </td>
                                        <td>
                                            <p>Government-issued identifying numbers, financial account details, genetic data,
                                                precise geolocation, race or ethnicity, religious or philosophical beliefs, union
                                                membership, mail, email, text messages, biometric data, health data, and sexual
                                                orientation or sex life.</p>
                                        </td>
                                        <td>
                                            <p className="!font-bold"></p>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <br />
                            <p className="!font-bold">
                                (B) <span className="underline">CATEGORIES OF SOURCES OF PERSONAL INFORMATION</span>
                            </p>
                            <ul>
                                <li><span className="underline">From activity on our website:</span> For example, when we collect
                                    your Usage Data automatically from analytic tools.</li>
                                <li><span className="underline">Directly from you:</span> For example, from forms you complete (such
                                    as the Data Subject Request), or when you contact us.
                                </li>
                                <li><span className="underline">From third-parties:</span> For example, when you interact with our
                                    advertising campaigns.</li>
                            </ul>
                            <br />
                            <p className="!font-bold">
                                (C) <span className="underline">USE OF PERSONAL INFORMATION</span>
                            </p>
                            <p>We may use the Personal Information collected as identified above, for the following purposes:
                            </p>
                            <ul>
                                <li>to fulfill or meet the reason you provided the Personal Information (support, respond to a
                                    query, etc.);</li>
                                <li>Monitor and improve our Services;</li>
                                <li>Provide our Services;</li>
                                <li>Track traffic in order to calculate conversion and business transactions with our Partners;
                                </li>
                                <li>Analyzing our Services and your use of the Services and website;</li>
                                <li>respond to law enforcement; or otherwise as detailed in our <Link
                                    to={ROUTES.PRIVACY_POLICY}>Privacy Policy</Link>.</li>
                            </ul>
                            <p>We will not collect additional categories of Personal Information or use the Personal Information
                                we collected for materially different, unrelated, or incompatible purposes without providing you
                                notice. </p>
                            <br />

                            <p className="!font-bold">
                                (D) <span className="underline">DISCLOSURES OF PERSONAL INFORMATION FOR A BUSINESS PURPOSE</span>
                            </p>
                            <p>We may disclose your Personal Information to a contractor or service provider for a business
                                purpose. When we disclose Personal Information for a business purpose, we enter a contract that
                                describes the purpose and requires the recipient to both keep that personal information
                                confidential and not use it for any purpose except performing the contract. We further restrict
                                the contractor and service provider from selling or sharing your Personal Information. In the
                                preceding twelve (12) months, we disclosed the following categories of Personal Information for
                                a business purpose: </p>
                            <table>
                                <tbody>
                                    <tr>
                                        <th>Business Purpose (as defined under CCPA)</th>
                                        <th style={{ width: '30%' }}>Category (corresponding with the table above)</th>
                                        <th style={{ width: '30%' }}>Category of Recipient</th>
                                    </tr>
                                    <tr>
                                        <td>
                                            <p>Helping to ensure security and integrity of our properties to the extent the use of
                                                your Personal Information is reasonably necessary and proportionate for these
                                                purposes.</p>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <p>Category A</p>
                                            <p>Category B</p>
                                            <p>Category D</p>
                                            <p>Category F</p>
                                            <p>Category G</p>
                                        </td>
                                        <td>
                                            <p>Affiliates, operational partner, security and fraud prevention providers, operating
                                                systems.</p>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <p>Debugging to identify and repair errors that impair existing intended functionality.
                                            </p>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <p>Category A</p>
                                            <p>Category D</p>
                                            <p>Category F</p>
                                            <p>Category G</p>
                                        </td>
                                        <td>
                                            <p>Analytic providers, operational partner, security and fraud prevention providers,
                                                operating systems.</p>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <p>Performing services on our behalf or service provider, including maintaining or
                                                servicing accounts, providing customer service, processing or fulfilling orders and
                                                transactions, verifying customer information, processing payments, providing
                                                financing, providing advertising or marketing services, providing analytic services,
                                                or providing similar services on our behalf or service provider</p>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <p>Category A</p>
                                            <p>Category B</p>
                                            <p>Category D</p>
                                            <p>Category F</p>
                                            <p>Category G</p>
                                        </td>
                                        <td>
                                            <p>Payment processors, subsidiaries, affiliated companies, operating systems, CRM, ERP,
                                                customer support, cloud computing and storage vendors, etc.</p>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <p>Undertaking internal research for technological development and demonstration.</p>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <p>Category A</p>
                                            <p>Category B</p>
                                            <p>Category D</p>
                                            <p>Category F</p>
                                            <p>Category G</p>
                                        </td>
                                        <td>
                                            <p>Developers, operating systems, cloud and hosting providers, SaaS platforms for task
                                                management and development, customer support and optimization tools.</p>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <p>
                                                Undertaking activities to verify or maintain the quality or safety of a service or
                                                device that is owned, or controlled by us, and to improve, upgrade, or enhance the
                                                service or device that is owned or controlled by us.
                                            </p>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <p>Category A</p>
                                            <p>Category D</p>
                                            <p>Category F</p>
                                            <p>Category G</p>
                                        </td>
                                        <td>
                                            <p>Developers, operating systems, cloud and hosting providers, SaaS platforms for task
                                                management and development, customer support and optimization tools. </p>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <br />

                            <p className="!font-bold">
                                (E) <span className="underline">SALE OR SHARE OF PERSONAL INFORMATION</span>
                            </p>
                            <p>In the preceding twelve (12) months, we do not “sell” information as most people would commonly
                                understand that term, we do not, and will not, disclose your Personal Information in direct
                                exchange for money or some other form of payment.</p>
                            <p>For retargeting and analytic purposes, when we promote our Services, we use third-party tools
                                that are able to market our Services online (“<strong>Campaigns</strong>”), measure those
                                Campaigns, identify individuals that are interested in our Services, etc. this is done by
                                placing cookies, pixel or other tracking technology on our website and sharing with these
                                vendors the online identifiers and online behavior information.</p>
                            <p>The CCPA defines “sharing” as “communicating orally, in writing, or by electronic or other means,
                                a consumer’s personal information” to “a third party for cross-context behavioral advertising,
                                whether or not for money or other valuable consideration”. In other words, we may share your
                                Personal Information with a third party to help promote our Services and understand your use of
                                our website.</p>
                            <p>In the preceding twelve (12) months, we “sell” or “share” the following categories of Personal
                                Information for a business purpose:</p>
                            <table style={{ display: 'inline-table' }}>
                                <tbody>
                                    <tr>
                                        <td>
                                            <p>Category (corresponding with the table above)</p>
                                        </td>
                                        <td>
                                            <p>Category Recipient</p>
                                        </td>
                                        <td>
                                            <p>Purpose of Sale or Share</p>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <p>Category A</p>
                                            <p>Category F</p>
                                            <p>Category G</p>
                                        </td>
                                        <td>
                                            <p>Marketing tools and vendors.</p>
                                        </td>
                                        <td>
                                            <p>Share for targeted advertising.</p>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <br />

                            <p className="!font-bold">
                                (F) <span className="underline">CHILDREN UNDER AGE 16</span>
                            </p>
                            <p>
                                We do not knowingly collect information from children under the age of 16.
                            </p>
                            <br />

                            <p className="!font-bold">
                                (G) <span className="underline">DATA RETENTION</span>
                            </p>
                            <p>
                                In general, we retain the Personal Data we collect for as long as it remains necessary for the
                                purposes set forth above, all under the applicable regulation, or until you express your
                                preference to optout, where applicable.
                            </p>
                            <p>
                                The retention periods are determined according to the following criteria:
                            </p>
                            <ol>
                                <li>
                                    <p>For as long as it remains necessary in order to achieve the purpose for which the
                                        Personal Data was initially processed. For example: if you contacted us, we will retain
                                        your contact information at least until we will address your inquiry.</p>
                                </li>
                                <li>
                                    <p>To comply with our regulatory obligations. For example: transactional data will be
                                        retained for up to seven years (or even more under certain circumstances) for compliance
                                        with our bookkeeping obligations purposes.</p>
                                </li>
                                <li>
                                    <p>To resolve a claim we might have or a dispute with you, including any legal proceeding
                                        between us, until such dispute will be resolved, and following, if we find it necessary,
                                        in accordance with applicable statutory limitation periods.</p>
                                </li>
                            </ol>
                            <p>Please note that except as required by applicable law, we will not be obligated to retain your
                                data for any particular period, and we may delete it for any reason and at any time, without
                                providing you with prior notice if our intention to do so.</p>
                            <br />

                            <h3 className="font-bold underline" id="part2">PART II: EXPLANATION OF YOUR RIGHTS UNDER THE CCPA AND
                                HOW TO EXERCISE THEM</h3>
                            <div className="font-bold">(H) <span className="underline">YOUR RIGHTS UNDER THE CCPA</span></div>
                            <p>If you are a California resident, you may exercise certain privacy rights related to your
                                Personal Information. You may exercise these rights free of charge except as otherwise permitted
                                under applicable law. We may limit our response to your exercise of these privacy rights as
                                permitted under applicable law, all as detailed herein and the Data Subject Request Form
                                available <a href="/data-subject-request-form/" target="_blank" rel="noreferrer"><strong>here</strong></a>.
                            </p>
                            <table className="text-center">
                                <tbody>
                                    <tr>
                                        <th>California Privacy Right</th>
                                        <th>Details</th>
                                    </tr>
                                    <tr>
                                        <td>
                                            <p>The right to know what Personal Information the business has collected.</p>
                                        </td>
                                        <td>
                                            <p>The right to know what Personal Information the business has collected about the
                                                consumer, including the categories of personal information, the categories of
                                                sources from which the Personal Information is collected, the business or commercial
                                                purpose for collecting, selling, or sharing Personal Information, the categories of
                                                third parties to whom the business discloses Personal Information, and the specific
                                                pieces of Personal Information the business has collected about the consumer.</p>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <p>Deletion Rights.</p>
                                        </td>
                                        <td>
                                            <p>The right to delete Personal Information that the business has collected from the
                                                consumer, subject to certain exceptions.</p>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <p>Correct Inaccurate Information</p>
                                        </td>
                                        <td>
                                            <p>The right to correct inaccurate Personal Information that a business maintains about
                                                a consumer</p>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <p>Opt-Out of Sharing for Cross-Contextual Behavioral Advertising</p>
                                        </td>
                                        <td>
                                            <p>You have the right to opt-out of the “sharing” of your personal information for
                                                “cross-contextual behavioral advertising,” often referred to as “interest-based
                                                advertising” or “targeted advertising.”</p>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <p>Opt-out from selling</p>
                                        </td>
                                        <td>
                                            <p>The right to opt-out of the sale or sharing of Personal Information by the business
                                            </p>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <p>Limit the Use or Disclosure of SPI</p>
                                        </td>
                                        <td>
                                            <p>Under certain circumstances, If the business uses or discloses SPI, the right to
                                                limit the use or disclosure of SPI by the business.</p>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <p>Opt-Out of the Use of Automated Decision Making</p>
                                        </td>
                                        <td>
                                            <p>In certain circumstances, you have the right to opt-out of the use of automated
                                                decision making in relation to your Personal Information.</p>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <p>Non-Discrimination</p>
                                        </td>
                                        <td>
                                            <p>The right not to receive discriminatory treatment by the business for the exercise of
                                                privacy rights conferred by the CCPA, including an employee’s, applicants, or
                                                independent contractor’s right not to be retaliated against for the exercise of
                                                their CCPA rights, denying a consumer goods or services, charging different prices
                                                or rates for goods or services, providing you a different level or quality of goods
                                                or services, etc. We may, however, charge different prices or rates, or provide a
                                                different level or quality of goods or services, if that difference is reasonably
                                                related to the value provided to us by your Personal Information.</p>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <p>Data Portability</p>
                                        </td>
                                        <td>
                                            <p>You may request to receive a copy of your Personal Information, including specific
                                                pieces of Personal Information, including, where applicable, to obtain a copy of the
                                                Personal Information you provided to us in a portable format.</p>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            <br />
                            <p>To learn more about your California privacy rights, please visit <a
                                href="https://oag.ca.gov/privacy/privacy-laws"
                                target="_blank" rel="noreferrer">https://oag.ca.gov/privacy/privacy-laws</a>.
                            </p>

                            <p>
                                <strong>(I) <u>HOW CAN YOU EXERCISE THE RIGHTS?</u></strong>
                            </p>
                            <p>You may exercise your rights through the DSR available <a href="/data-subject-request-form/"
                                target="_blank" rel="noreferrer">here</a>. The instructions for submitting, the general description of the
                                process, verification requirements, when applicable, including any information the consumer must
                                provide are all detailed in the DSR.</p>
                            <p>
                                Note, certain rights can be done by you independently without using the DSR. For example,
                                depending on your interaction with us:
                            </p>
                            <ul>
                                <li>
                                    you can opt-out from receiving emails from us by clicking the “unsubscribe” link within the
                                    email; and
                                </li>
                            </ul>
                            <p>Further, opt-out rights can be executed without filling the form:</p>
                            <ul>
                                <li>through the cookie settings available on our website;</li>
                                <li>
                                    through device settings (opt-out from tracking AAID, ADID, please see the following for
                                    information applicable to all devices: <a href="https://thenai.org/opt-out/mobile-opt-out/"
                                        target="_blank" rel="noreferrer">https://thenai.org/opt-out/mobile-opt-out/</a>);
                                </li>
                                <li>
                                    by using Self-Regulatory Program for Online Behavioral Advertising such as:
                                    Digital Advertising Alliance’s (“<strong>DAA</strong>”): <a
                                        href="https://www.aboutads.info/choices"
                                        target="_blank" rel="noreferrer">https://www.aboutads.info/choices</a>
                                    Network Advertising Initiative (“<strong>NAI</strong>”): <a
                                        href="https://www.networkadvertising.org/choices"
                                        target="_blank" rel="noreferrer">https://www.networkadvertising.org/choices</a>.
                                </li>
                                <li>
                                    install privacy-controls in the browser's settings to automatically signal the opt-out
                                    preference to all websites you visit (like the “<a href="https://globalprivacycontrol.org/"
                                        target="_blank" rel="noreferrer">Global Privacy Control</a>”). We honor the Global Privacy Control, where
                                    applicable, subject to your jurisdiction, as a valid request to opt-out of the sharing of
                                    information linked to your browser.
                                </li>
                            </ul>
                            <br />
                            <p>
                                <strong>(J) <span style={{ textDecoration: 'underline' }}>AUTHORIZED AGENTS</span></strong>
                            </p>
                            <p>“Authorized agents” may submit opt out requests on a consumer’s behalf. If you have elected to
                                use an authorized agent, or if you are an authorized agent who would like to submit requests on
                                behalf of a consumer, the following procedures will be required prior to acceptance of any
                                requests by an authorized agent on behalf of a California consumer. Usually, we will accept
                                requests from qualified third parties on behalf of other consumers, regardless of either the
                                consumer or the authorized agent’s state of residence, provided that the third party
                                successfully completes the following qualification procedures:</p>
                            <ol style={{ listStyleType: 'lower-latin' }}>
                                <li>
                                    <p>When a consumer uses an authorized agent to submit a request to know or a request to
                                        delete, a business may require that the consumer do the following:</p>
                                    <ul>
                                        <li>
                                            <p>Provide the authorized agent signed permission to do so or power of attorney.</p>
                                        </li>
                                        <li>
                                            <p>Verify their own identity directly with the business.</p>
                                        </li>
                                        <li>
                                            <p>Directly confirm with the business that they provided the authorized agent
                                                permission to submit the request.</p>
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <p>A business may deny a request from an authorized agent that does not submit proof that
                                        they have been authorized by the consumer to act on their behalf.</p>
                                </li>
                            </ol>
                            <div>
                                <div className="!font-bold">(K) <span className="underline">NOTICE OF FINANCIAL INCENTIVE</span>
                                </div>
                            </div>
                            <p>
                                We may, at times, offer you various financial incentives such as promo codes, discounts and
                                special offers when you register to our newsletter by providing us with contact information and
                                identifiers such as your name and email address. When you sign-up for our newsletter, email list
                                or other discounts and special offers, you opt-in to a financial incentive. You may withdraw
                                from a financial incentive at any time by opting out through the unsubscribe link within the
                                email. Generally, we do not assign monetary or other value to Personal Information, however,
                                California law requires that we assign such value in the context of financial incentives. In
                                such context, the value of the Personal Information is related to the estimated cost of
                                providing the associated financial incentive(s) for which the information was collected. You can
                                end program participation at any time by contacting us at <a href="mailto:dpo@ryzebeyond.com"
                                    target="_blank" rel="noreferrer">dpo@ryzebeyond.com</a> to unsubscribe or cancel your participation in any
                                special offer. We will not discriminate against you, in any manner prohibited by applicable law,
                                for exercising these rights.
                            </p>
                            <br />

                            <div className="!font-bold underline">CONTACT US</div>
                            <p>
                                Ryze Beyond Ltd.<br />
                                <a href="mailto:dpo@ryzebeyond.com" target="_blank" rel="noreferrer">dpo@ryzebeyond.com</a>
                            </p>
                            <br />

                            <p>
                                <strong><u>UPDATES:</u></strong>
                                <br />
                                This notice was last updated on February 11, 2025, as required under the CCPA, we will
                                <strong>update</strong> the CCPA Notice <strong>every 12 months</strong>. The last revision date
                                will be reflected in the “Last Modified” heading at the top of this CCPA Notice.
                            </p>
                            <br />

                            <h2>
                                <strong><u>PART III: OTHER CALIFORNIA OBLIGATIONS</u></strong>
                            </h2>
                            <p>
                                <strong><u>Direct Marketing Requests:</u></strong>
                                California Civil Code Section 1798.83 permits you, if you are a California resident, to request
                                certain information regarding disclosure of Personal Information to third parties for their
                                direct marketing purposes. To make such a request, please use the Data Subject Request Form
                                available <a href="/data-subject-request-form/" target="_blank" rel="noreferrer"><u>here</u></a>.
                            </p>

                            <p>
                                <strong><u>Do Not Track Settings:</u></strong>
                                Cal. Bus. And Prof. Code Section 22575 also requires us to notify you how we deal with the “Do
                                Not Track” settings in your browser. As of the effective date listed above, there is no commonly
                                accepted response for Do Not Track signals initiated by browsers. Therefore, we so not respond
                                to the Do Not Track settings. Do Not Track is a privacy preference you can set in your web
                                browser to indicate that you do not want certain information about your web page visits tracked
                                and collected across websites. For more details, including how to turn on Do Not Track, visit:
                                <a href="http://www.donottrack.us" target="_blank" rel="noreferrer">www.donottrack.us</a>
                            </p>

                            <p>
                                <strong><u>California’s “Shine the Light” law (Civil Code Section § 1798.83):</u></strong>
                                permits employees that are California residents to request certain information regarding our
                                disclosure of personal information to third parties for their direct marketing purposes. To make
                                such a request, please send us the Data Subject Request Form available <a
                                    href="/data-subject-request-form/" target="_blank" rel="noreferrer">here</a>.
                            </p>
                        </div>
                    </div>

                </div>
            </main>
        </div>
    )
}

export default CCPAPrivacyNoticePage